/** Admin — Supplier Requests: on-request booking pipeline with deposit locking. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, X, Loader2, Send, Ban, Plus, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Modal, CATEGORY_LABEL } from "./SuppliersPanel";

const FIELD = "w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] outline-none";
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ");
  return "";
};

const STATUS = {
  pending_assignment: { label: "Needs supplier", cls: "border-zinc-500/50 text-zinc-300" },
  pending_supplier: { label: "Pending supplier confirmation", cls: "border-amber-400/50 text-amber-300" },
  accepted: { label: "Accepted — awaiting deposit", cls: "border-blue-400/50 text-blue-300" },
  confirmed: { label: "Confirmed & locked", cls: "border-emerald-400/50 text-emerald-300" },
  declined: { label: "Declined", cls: "border-red-400/50 text-red-300" },
  expired: { label: "Deposit expired", cls: "border-orange-400/50 text-orange-300" },
  cancelled: { label: "Cancelled", cls: "border-zinc-600 text-zinc-500" },
};
const TABS = ["all", "pending_assignment", "pending_supplier", "accepted", "confirmed", "declined"];

export default function SupplierRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("all");
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/supplier-requests"); setRequests(data || []); }
    catch { /* soft */ }
  };
  useEffect(() => { load(); }, []);

  const resend = async (r) => {
    setBusy(r.id + "alert");
    try {
      const { data } = await api.post(`/admin/supplier-requests/${r.id}/send-alert`);
      toast.success(`Alert poslan na ${data.sent_to}`);
      load();
    } catch (e) { toast.error(errMsg(e) || "Slanje nije uspjelo"); }
    finally { setBusy(null); }
  };
  const cancel = async (r) => {
    setBusy(r.id + "cancel");
    try {
      await api.patch(`/admin/supplier-requests/${r.id}`, { status: "cancelled" });
      toast.success("Zahtjev otkazan");
      load();
    } catch (e) { toast.error(errMsg(e) || "Nije uspjelo"); }
    finally { setBusy(null); }
  };

  const shown = requests.filter((r) => tab === "all" || r.status === tab);

  return (
    <div data-testid="supplier-requests-panel" className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Inbox className="w-4 h-4 text-[#C5A059]" />
        <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400">Supplier Requests — On-Request Bookings</h2>
        <button data-testid="create-supplier-request-btn" onClick={() => setCreating(true)}
          className="ml-auto px-4 h-9 rounded-full border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> New Request
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t} data-testid={`req-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 h-8 rounded-full text-[10px] uppercase tracking-widest border ${tab === t ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-zinc-500"}`}>
            {t === "all" ? `All (${requests.length})` : `${STATUS[t]?.label || t} (${requests.filter((r) => r.status === t).length})`}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {shown.length === 0 && <p className="text-sm text-zinc-500">No requests in this view.</p>}
        {shown.map((r) => (
          <div key={r.id} data-testid={`supplier-request-${r.id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <p className="text-white text-sm flex items-center gap-2 flex-wrap">
                <span className="text-[#C5A059]">{r.ref}</span>
                {r.service_title || CATEGORY_LABEL[r.category] || r.category}
                <span data-testid={`req-status-${r.id}`} className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${(STATUS[r.status] || {}).cls || "border-white/15 text-zinc-400"}`}>
                  {(STATUS[r.status] || {}).label || r.status}
                </span>
                <span className="text-[9px] uppercase tracking-widest text-zinc-600">{r.source}</span>
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {r.customer_name} · {r.customer_whatsapp || r.customer_email} · {r.service_date || "no date"}{r.service_time ? ` ${r.service_time}` : ""} · {r.guests || "?"} pax
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {r.supplier_name ? `→ ${r.supplier_name}` : "→ no supplier assigned"} · total €{Number(r.total_price_eur || 0).toFixed(2)} · deposit €{Number(r.deposit_eur || 0).toFixed(2)} ({Number(r.deposit_pct || 0)}%)
              </p>
            </div>
            {["pending_assignment", "pending_supplier"].includes(r.status) && (
              <>
                <button data-testid={`req-assign-${r.id}`} onClick={() => setAssigning(r)}
                  className="h-9 px-3 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
                  <Pencil className="w-3.5 h-3.5" /> {r.supplier_id ? "Edit / Price" : "Assign supplier"}
                </button>
                {r.supplier_id && (
                  <button data-testid={`req-alert-${r.id}`} onClick={() => resend(r)} disabled={busy === r.id + "alert"}
                    className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
                    {busy === r.id + "alert" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send alert
                  </button>
                )}
              </>
            )}
            {!["confirmed", "cancelled", "declined"].includes(r.status) && (
              <button data-testid={`req-cancel-${r.id}`} onClick={() => cancel(r)} disabled={busy === r.id + "cancel"}
                className="h-9 px-3 rounded-full border border-red-500/40 text-red-400 text-xs uppercase tracking-widest flex items-center gap-1">
                {busy === r.id + "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Cancel
              </button>
            )}
          </div>
        ))}
      </div>

      {creating && <RequestModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {assigning && <RequestModal existing={assigning} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(); }} />}
    </div>
  );
}

function RequestModal({ existing, onClose, onDone }) {
  const isEdit = !!existing;
  const [suppliers, setSuppliers] = useState([]);
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    supplier_id: existing?.supplier_id || "",
    service_id: existing?.service_id || "",
    service_title: existing?.service_title || "",
    category: existing?.category || "boats",
    customer_name: existing?.customer_name || "",
    customer_email: existing?.customer_email || "",
    customer_whatsapp: existing?.customer_whatsapp || "",
    service_date: existing?.service_date || "",
    service_time: existing?.service_time || "",
    guests: existing?.guests || 2,
    notes: existing?.notes || "",
    total_price_eur: existing?.total_price_eur || "",
    send_alert: true,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/admin/suppliers").then(({ data }) => setSuppliers((data || []).filter((s) => s.status === "active"))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.supplier_id) { setServices([]); return; }
    api.get(`/admin/suppliers/${f.supplier_id}/services`).then(({ data }) => setServices(data || [])).catch(() => setServices([]));
  }, [f.supplier_id]);

  const go = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        const payload = {
          supplier_id: f.supplier_id || undefined,
          service_id: f.service_id || undefined,
          service_title: f.service_title || undefined,
          total_price_eur: f.total_price_eur === "" ? undefined : Number(f.total_price_eur),
          service_date: f.service_date, service_time: f.service_time,
          guests: Number(f.guests) || 0, notes: f.notes,
        };
        await api.patch(`/admin/supplier-requests/${existing.id}`, payload);
        if (f.supplier_id && f.send_alert) await api.post(`/admin/supplier-requests/${existing.id}/send-alert`);
        toast.success("Request updated" + (f.supplier_id && f.send_alert ? " — alert poslan supplieru" : ""));
      } else {
        await api.post("/admin/supplier-requests", {
          ...f,
          supplier_id: f.supplier_id || null,
          service_id: f.service_id || null,
          guests: Number(f.guests) || 0,
          total_price_eur: f.total_price_eur === "" ? null : Number(f.total_price_eur),
        });
        toast.success("Request created" + (f.supplier_id && f.send_alert ? " — alert poslan supplieru" : ""));
      }
      onDone();
    } catch (e) { toast.error(errMsg(e) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg text-white font-light">{isEdit ? `Request ${existing.ref}` : "New Supplier Request"}</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Supplier</label>
          <select data-testid="req-form-supplier" value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value, service_id: "" })} className={FIELD}>
            <option value="">— assign later —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.business_name} ({CATEGORY_LABEL[s.category] || s.category}, {Number(s.commission_pct)}%)</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Service</label>
          <select data-testid="req-form-service" value={f.service_id} onChange={(e) => {
            const sv = services.find((x) => x.id === e.target.value);
            setF({ ...f, service_id: e.target.value, service_title: sv?.title || f.service_title, total_price_eur: sv ? sv.price_from_eur : f.total_price_eur });
          }} className={FIELD} disabled={!f.supplier_id}>
            <option value="">— custom —</option>
            {services.map((sv) => <option key={sv.id} value={sv.id}>{sv.title} (€{Number(sv.price_from_eur).toFixed(0)})</option>)}
          </select></div>
        {!f.service_id && (
          <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Custom service title</label>
            <input data-testid="req-form-title" value={f.service_title} onChange={set("service_title")} className={FIELD} /></div>
        )}
        {!isEdit && (
          <>
            <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Customer name *</label>
              <input data-testid="req-form-name" value={f.customer_name} onChange={set("customer_name")} className={FIELD} /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Customer email *</label>
              <input data-testid="req-form-email" value={f.customer_email} onChange={set("customer_email")} className={FIELD} /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Customer WhatsApp</label>
              <input data-testid="req-form-whatsapp" value={f.customer_whatsapp} onChange={set("customer_whatsapp")} className={FIELD} /></div>
          </>
        )}
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Date</label>
          <input data-testid="req-form-date" type="date" value={f.service_date} onChange={set("service_date")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Time</label>
          <input data-testid="req-form-time" type="time" value={f.service_time} onChange={set("service_time")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Guests</label>
          <input data-testid="req-form-guests" type="number" min="0" value={f.guests} onChange={set("guests")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Total price € (override)</label>
          <input data-testid="req-form-price" type="number" min="0" value={f.total_price_eur} onChange={set("total_price_eur")} className={FIELD} /></div>
        <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Notes</label>
          <input data-testid="req-form-notes" value={f.notes} onChange={set("notes")} className={FIELD} /></div>
      </div>
      {f.supplier_id && (
        <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer mt-4">
          <input type="checkbox" data-testid="req-form-send-alert" checked={f.send_alert} onChange={(e) => setF({ ...f, send_alert: e.target.checked })} className="accent-[#C5A059]" />
          Send instant alert (Email + WhatsApp link) to the supplier now
        </label>
      )}
      <button data-testid="req-form-submit" onClick={go}
        disabled={busy || (!isEdit && (!f.customer_name || !f.customer_email))}
        className="w-full h-11 rounded-xl bg-[#C5A059] text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-50 mt-4 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {isEdit ? "Save & dispatch" : "Create request"}
      </button>
    </Modal>
  );
}
